/**
 * Copy-to-clipboard with a legacy fallback: the async Clipboard API requires a secure context,
 * which a self-hosted LAN deployment over plain http is not, so the execCommand path keeps the
 * copy-link button working there. Both collaborators are injectable for tests.
 */

/** Structural slice of Navigator.clipboard the copy path needs. */
export interface ClipboardWriter {
  writeText(text: string): Promise<void>;
}

/**
 * Structural slice of the textarea-based legacy copy path. The area type is generic so the real
 * DOM surface (HTMLTextAreaElement) stays assignable without casts while tests use plain fakes.
 */
export interface CopyTextArea {
  value: string;
  setAttribute(name: string, value: string): void;
  style: { position: string; opacity: string };
  select(): void;
  remove(): void;
}

export interface CopySurface<TArea extends CopyTextArea> {
  makeTextArea(): TArea;
  appendToBody(node: TArea): void;
  execCopy(): boolean;
}

/** Returns the navigator clipboard when present, null otherwise (insecure context, old browser). */
export function navigatorClipboard(): ClipboardWriter | null {
  const clipboard = navigator.clipboard;
  if (clipboard === undefined) {
    return null;
  }
  return clipboard;
}

/** Adapts a real Document to the copy surface. */
export function documentCopySurface(doc: Document): CopySurface<HTMLTextAreaElement> {
  return {
    makeTextArea: (): HTMLTextAreaElement => doc.createElement('textarea'),
    appendToBody: (node: HTMLTextAreaElement): void => {
      doc.body.appendChild(node);
    },
    execCopy: (): boolean => doc.execCommand('copy'),
  };
}

/**
 * Copies text using the Clipboard API, falling back to the legacy textarea + execCommand path
 * when the API is missing or rejects. Resolves false only when both paths fail.
 */
export async function copyText(
  text: string,
  writer: ClipboardWriter | null = navigatorClipboard(),
  surface: CopySurface<CopyTextArea> = documentCopySurface(document),
): Promise<boolean> {
  if (writer !== null) {
    try {
      await writer.writeText(text);
      return true;
    } catch {
      // fall through to the legacy path
    }
  }
  return legacyCopy(text, surface);
}

/** Deprecated-but-universal fallback: a hidden selected textarea plus execCommand('copy'). */
function legacyCopy(text: string, surface: CopySurface<CopyTextArea>): boolean {
  const area = surface.makeTextArea();
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  surface.appendToBody(area);
  area.select();
  let copied = false;
  try {
    copied = surface.execCopy();
  } catch {
    copied = false;
  }
  area.remove();
  return copied;
}
